import { useParams } from 'react-router-dom';
import { getUpdatedPayload } from 'utils/helpers';
import { useCallback, useEffect, useState } from 'react';
import { Form, FormInstance, FormProps } from 'components';
import { useSupplierPricingRulesUpdateMutation } from 'api';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { IEditPricing, SupplierPricingRuleType } from './index';

export const useEditPricing = (props: IEditPricing) => {
  const { onModalStateChange, modalState, dataToEdit } = props;
  const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(true);
  const [type, setType] = useState<string>('WEEK');

  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const [updatePricingRules, { isLoading }] = useSupplierPricingRulesUpdateMutation();
  const { handleError, handleSuccess } = useNotificationMessages();

  const handleTypeChange = useCallback((type: string) => {
    setDisableSaveBtn(false);
    setType(type);
  }, []);

  const clearFieldValidation = useCallback((form: FormInstance, field: string) => {
    const hasError = form.getFieldError(field)?.length;
    if (hasError) {
      form.setFields([
        {
          name: field,
          errors: [],
        },
      ]);
    }
  }, []);

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
    setDisableSaveBtn(true);
  }, [form, onModalStateChange]);

  const onFinish: FormProps<SupplierPricingRuleType>['onFinish'] = useCallback(
    async (values: SupplierPricingRuleType) => {
      const payload = getUpdatedPayload(values, dataToEdit);
      payload.id = id;
      payload.type = type;
      try {
        const { data, error }: Record<string, any> = await updatePricingRules(payload);
        if (data?.success) {
          handleSuccess('Pricing rules was successfully updated.');
          onCancel();
        } else {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [updatePricingRules, id, onCancel, dataToEdit, type, handleError, handleSuccess]
  );

  useEffect(() => {
    if (modalState && dataToEdit) {
      form.setFieldsValue(dataToEdit);
      if (dataToEdit.type) {
        setType(dataToEdit.type);
      }
    }
  }, [modalState, form, dataToEdit]);

  return {
    form,
    isLoading,
    disableSaveBtn,
    clearFieldValidation,
    onFinish,
    onCancel,
    type,
    handleTypeChange,
  };
};
